/**
 * The visitor's consent decisions, mirrored into `sessionStorage` on the widget
 * origin — the caller `consent.ts` was written for and did not have.
 *
 * ── WHY THIS IS A SEPARATE FILE AND NOT PART OF `consent.ts` ────────────────
 *
 * `consent.ts` refuses to touch storage, and its header gives two reasons. The
 * second is the one that produced this file: "WHERE a consent record lives is a
 * decision with its own cost, and it belongs to the caller that already owns
 * the other two records this app keeps. {@link serializeConsent} and
 * {@link parseConsent} exist so that caller can mirror the value into
 * `sessionStorage` on the WIDGET origin without this module deciding for it."
 *
 * This is that caller. It is a separate module rather than three lines inside
 * `useWidget.ts` for the reason the first reason gives: the unit runner for this
 * app is a hermetic node environment (`packages/config-vitest/src/index.js`,
 * `environment: 'node'`), and a persistence rule written inside a component or a
 * composable is a rule no test can reach.
 *
 * ── `sessionStorage`, AND WHY NOT `localStorage` ────────────────────────────
 *
 * `api/session-store.ts` and `app/seen.ts` both argue for `sessionStorage` in
 * writing, and the argument applies here one step harder. A widget's audience is
 * frequently on a shared or public machine, and a DURABLE record of what a named
 * visitor agreed to is worse to leave behind than a read pointer or a transcript
 * that is theirs anyway. `visitor-context.ts` also refuses
 * `cross-site-identifier` by name, with the note that "an identifier that
 * outlives the tab is one field away from being a tracker, and the field is
 * usually added by someone fixing a re-prompt" — a `localStorage` consent record
 * is exactly the field, and exactly that fix.
 *
 * THE COST IS NAMED AND IT IS REAL: the visitor is asked again in a new tab.
 * That is annoying, and it is the smaller wrong. The alternative is a durable
 * per-visitor record on our origin whose only job is to stop us asking a
 * question — and the question is the part that protects them.
 *
 * ── EVERY ACCESS ABSORBS EVERY FAILURE, AND DEGRADES TO "NO DECISION" ───────
 *
 * `window.sessionStorage` THROWS on access in some privacy modes rather than
 * returning null — the same failure `session-store.ts` and `seen.ts` both
 * absorb. Here the fallback is not merely lossy, it is the SAFE direction:
 * {@link readConsent} answers `noConsent()`, `isGranted` answers false for every
 * purpose, and `buildVisitorContext` omits both optional fields. A visitor who
 * hardened their browser gets a widget that works and collects less, which is
 * the only ordering of those two outcomes that is defensible.
 *
 * There is deliberately no attempt to detect the difference between "storage is
 * unavailable" and "nothing stored yet". Both mean the visitor is asked, and a
 * third state would be a state some later branch could get wrong.
 */
import { noConsent, parseConsent, serializeConsent, type ConsentState } from './consent';

/**
 * Namespaced by public key, exactly like the session cache and for the same
 * reason: two brands' widgets on one page are two notices and two decisions, and
 * a shared key would let a visitor's answer to one brand speak for the other.
 *
 * NOT namespaced by notice version. A bump must OVERWRITE the old record rather
 * than sit beside it — `isGranted` already treats a decision carried against a
 * different `noticeVersion` as no decision, so a version in the key would only
 * accumulate dead records of superseded answers, which is a small archive of
 * what a person used to agree to.
 */
function storageKey(publicKey: string): string {
  return `omni.widget.consent.${publicKey}`;
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch (unavailable) {
    if (unavailable instanceof Error) {
      return null;
    }
    throw unavailable;
  }
}

/**
 * What this visitor has decided, in this tab, under the CURRENT notice.
 *
 * The version filtering is not done here — `parseConsent` keeps a row whose
 * `noticeVersion` is stale and `isGranted`/`wasAsked` are what discard it. That
 * split is deliberate: this function's job is "what is written down", and
 * deciding what still counts is a rule that must live in exactly one place.
 */
export function readConsent(publicKey: string): ConsentState {
  const store = storage();
  if (store === null) {
    return noConsent();
  }
  const raw = store.getItem(storageKey(publicKey));
  if (raw === null) {
    return noConsent();
  }
  return parseConsent(raw);
}

/**
 * Write the decisions down. A failure is swallowed, and the visitor is asked
 * again next time.
 *
 * Storage is full, or this browser counts `setItem` as tracking. Neither is
 * worth failing the widget over, and neither may be worth PRETENDING succeeded:
 * the caller holds the state in a ref either way, so the decision is honoured
 * for this frame's life and forgotten on the next navigation. Forgetting a `yes`
 * costs a re-prompt; forgetting a `no` costs a re-prompt as well, because
 * absence is not a grant.
 */
export function writeConsent(publicKey: string, state: ConsentState): void {
  const store = storage();
  if (store === null) {
    return;
  }
  try {
    store.setItem(storageKey(publicKey), serializeConsent(state));
  } catch (quotaOrPrivacy) {
    if (!(quotaOrPrivacy instanceof Error)) {
      throw quotaOrPrivacy;
    }
  }
}

/**
 * Remove the record entirely — the storage half of `forgetConsent()`.
 *
 * `removeItem` and not "write an empty state". `consent.ts` says why: "the
 * record of a past answer is itself a record about a person, so an erasure that
 * kept the consent log would be keeping the one thing the visitor was most
 * explicitly done with." An empty `{"decisions":[]}` left behind would still be
 * a key on our origin naming this widget, which is the artefact being removed.
 */
export function forgetStoredConsent(publicKey: string): void {
  const store = storage();
  if (store === null) {
    return;
  }
  store.removeItem(storageKey(publicKey));
}
